import * as React from 'react';
import { Box, Typography } from '@mui/material';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { useTranslation } from 'react-i18next';
import { HOUR_HEIGHT, UNAVAILABILITY_CONFIG } from './calendarConstants';
import useCalendarState from './hooks/useCalendarState';
import DraggableEvent from './DraggableEvent';
import { UnavailabilityTooltipContent } from './EventTooltipContent';

// Format hour to HH:MM string
const formatHour = (hour) => {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

function UnavailabilityEvent({ unavailability, startHour: workStartHour = 8, endHour: workEndHour = 16, dayIndex, overlapIndex = 0, overlapTotal = 1, employees: employeesProp = [], showEmployeeName = false }) {
  const { t } = useTranslation();
  const { openEditUnavailabilityDialog, employees: storeEmployees } = useCalendarState();
  const employees = employeesProp.length > 0 ? employeesProp : storeEmployees;
  // Use pre-calculated clamped hours from CalendarGrid (already clamped to working hours)
  const eventStartHour = unavailability._clampedStartHour;
  const eventEndHour = unavailability._clampedEndHour;

  const durationHours = eventEndHour - eventStartHour;

  // Skip rendering if no duration (should already be filtered by CalendarGrid)
  if (durationHours <= 0) {
    return null;
  }

  const top = (eventStartHour - workStartHour) * HOUR_HEIGHT + 2;
  const height = Math.max(durationHours * HOUR_HEIGHT - 4, 20); // Minimum height of 20px

  const config = UNAVAILABILITY_CONFIG[unavailability.type] || UNAVAILABILITY_CONFIG.vacation;

  // Get translated type label
  const typeLabel = t(`unavailability_type_${unavailability.type}`);

  // Find employee name if needed
  const employee = showEmployeeName ? employees.find((e) => e.id === unavailability.employee_id) : null;
  const employeeName = employee?.name || '';

  // Format timespan using original (unclamped) hours
  const timespan = `${formatHour(unavailability.local_start_hour)} - ${formatHour(unavailability.local_end_hour)}`;

  // Only show title if it's not empty
  const hasTitle = unavailability.title && unavailability.title.trim() !== '';

  const handleClick = (e) => {
    e.stopPropagation();
    openEditUnavailabilityDialog(unavailability);
  };

  // Resolve employee name for tooltip (always, regardless of showEmployeeName)
  const tooltipEmployeeName = employees.find((e) => e.id === unavailability.employee_id)?.name;

  const tooltipContent = (
    <UnavailabilityTooltipContent
      typeLabel={typeLabel}
      config={config}
      employeeName={tooltipEmployeeName}
      timespan={timespan}
      title={hasTitle ? unavailability.title : null}
    />
  );

  return (
    <DraggableEvent
      event={unavailability}
      eventType="unavailability"
      top={top}
      height={height}
      backgroundColor={config.bg}
      borderColor={config.color}
      onClick={handleClick}
      dayIndex={dayIndex}
      gridStartHour={workStartHour}
      gridEndHour={workEndHour}
      overlapIndex={overlapIndex}
      overlapTotal={overlapTotal}
      tooltipContent={tooltipContent}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <BeachAccessIcon sx={{ fontSize: 14, color: config.color, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: config.color,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {showEmployeeName && employeeName ? employeeName : typeLabel}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '10px',
            color: config.color,
            opacity: 0.8,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {timespan}
          {showEmployeeName && ` • ${typeLabel}`}
          {hasTitle && ` • ${unavailability.title}`}
        </Typography>
      </Box>
    </DraggableEvent>
  );
}

export default UnavailabilityEvent;
